import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { TypeSaleType, TypeSaleTypeLabel } from "../enum/saleEnums";

interface SaleItem {
  sale_item_id: number;
  quantity: number;
  unit_price: string;
  subtotal: string;
  product: {
    product_name: string;
    product_barcode: string;
  };
}

interface SaleDetail {
  sale_id: number;
  sale_number: number;
  created_at: string;
  total: string;
  sale_type?: TypeSaleType;
  due_date?: string | null;
  client_name?: string | null;
  client_document?: string | null;
  payment_status?: "PENDING" | "OVERDUE" | "SETTLED" | "CANCELED" | null;
  payment_method?: string | null;
  user: {
    user_name: string;
  };
  items: SaleItem[];
}

// due_date chega como campo DATE (sem hora) — monta pelos componentes pra
// não deixar o navegador interpretar como UTC (mesmo cuidado de
// PendingPaymentPopup/Receipt).
const formatDateOnly = (value: string) => {
  const [year, month, day] = (value ?? "").slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: "Pendente",
  OVERDUE: "Vencida",
  SETTLED: "Paga",
  CANCELED: "Cancelada"
};

interface SaleListModalProps {
  open: boolean;
  onClose: () => void;
  sale_id?: number;
}

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70vw",
  maxHeight: "90vh",
  bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
  boxShadow: 24,
  p: 4,
  overflowY: "auto" as const,
  borderRadius: 2
};

const SaleListModal: React.FC<SaleListModalProps> = ({
  open,
  onClose,
  sale_id
}) => {
  const [sale, setSale] = useState<SaleDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open || sale_id == null) return;

    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const res = await api.get<SaleDetail>(`/sales/${sale_id}`);
        setSale(res.data);
      } catch (err) {
        console.error(err);
        setErrorMsg("Erro ao carregar detalhes da venda.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, sale_id]);

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby="sale-detail-modal">
        <Box sx={style}>
          <Typography variant="h6" mb={2}>
            Detalhes da Venda {sale?.sale_number ?? ""}
          </Typography>

          {loading && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && sale && (
            <>
              <Box mb={2}>
                <Typography>
                  <strong>Data:</strong>{" "}
                  {new Date(sale.created_at).toLocaleString("pt-BR")}
                </Typography>
                <Typography>
                  <strong>Usuário:</strong> {sale.user.user_name}
                </Typography>
                <Typography>
                  <strong>Total:</strong>{" "}
                  {Number(sale.total).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  })}
                </Typography>
                {sale.sale_type && (
                  <Typography>
                    <strong>Pagamento:</strong> {TypeSaleTypeLabel[sale.sale_type] ?? sale.sale_type}
                    {sale.sale_type === TypeSaleType.DEFERRED && sale.payment_status && (
                      <> — {PAYMENT_STATUS_LABEL[sale.payment_status] ?? sale.payment_status}</>
                    )}
                  </Typography>
                )}
                {sale.sale_type === TypeSaleType.DEFERRED && sale.due_date && (
                  <Typography>
                    <strong>Previsto para:</strong> {formatDateOnly(sale.due_date)}
                  </Typography>
                )}
                {(sale.client_name || sale.client_document) && (
                  <Typography>
                    <strong>Cliente:</strong> {sale.client_name || "Avulso"}
                    {sale.client_document ? ` — ${sale.client_document}` : ""}
                  </Typography>
                )}
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell>Qtd.</TableCell>
                    <TableCell>Valor Unit.</TableCell>
                    <TableCell>Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sale.items.map((item) => (
                    <TableRow key={item.sale_item_id}>
                      <TableCell>
                        {item.product.product_barcode} ::{" "}
                        {item.product.product_name}
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {Number(item.unit_price).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL"
                        })}
                      </TableCell>
                      <TableCell>
                        {Number(item.subtotal).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL"
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box mt={3} textAlign="right">
                <Button variant="contained" onClick={onClose}>
                  Fechar
                </Button>
              </Box>
            </>
          )}

          {!loading && !sale && !errorMsg && (
            <Typography>Nenhum dado para exibir.</Typography>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default SaleListModal;
